import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { notification } from 'antd'
import { formatEndDate, formatStartDate } from '@/utils/format'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import {
  deleteEvent,
  fetchEvents,
  getEventById,
  updateEvent,
} from '@/api/events.api'
import type { EventItem } from '@/types/api'
import { buildColumns } from './columns'
import { EventFilterPanel } from './EventFilterPanel'
import type { EventFilterValues } from './EventFilterPanel'
import { EventDetailDrawer } from './EventDrawer'
import { EventFormDrawer } from './EventFormDrawer'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { downloadFileWithName } from '@/utils/download'
import { usePermission } from '@/hooks/use-permission'
import { exportEvent, useTableExport } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: EventFilterValues = {
  keySearch: '',
  unitId: undefined,
  status: undefined,
  time: undefined,
  timeUpdate: undefined,
}

export function EventsPage() {
  const queryClient = useQueryClient()
  const [notificationApi, contextHolder] = notification.useNotification()
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const canView = usePermission('event-view')
  const canCreate = usePermission('event-create')
  const canUpdate = usePermission('event-update')
  const canExport = usePermission('event-export')

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<EventFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<EventFilterValues>(DEFAULT_FILTER)
  const [isExporting, setIsExporting] = useState(false)

  // Modal & Form state
  const [viewingEvent, setViewingEvent] = useState<EventItem | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null)
  const [deletingEvent, setDeletingEvent] = useState<EventItem | null>(null)
  const [togglingEvent, setTogglingEvent] = useState<EventItem | null>(null)

  const filterParams = useMemo(() => {
    return {
      page: pageIndex,
      size: pageSize,
      keySearch: activeFilters.keySearch || undefined,
      unitId: showUnitColumn ? activeFilters.unitId : currentUser?.unitId,
      status: activeFilters.status,
      startDateFrom: formatStartDate(activeFilters.time?.from) || undefined,
      startDateTo: formatEndDate(activeFilters.time?.to) || undefined,
      updatedAtFrom:
        formatStartDate(activeFilters.timeUpdate?.from) || undefined,
      updatedAtTo: formatEndDate(activeFilters.timeUpdate?.to) || undefined,
    }
  }, [pageIndex, pageSize, activeFilters, showUnitColumn, currentUser?.unitId])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['events', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) =>
      fetchEvents(
        pageIndex,
        pageSize,
        {
          keySearch: filterParams.keySearch,
          unitId: filterParams.unitId,
          status: filterParams.status,
          startDateFrom: filterParams.startDateFrom,
          startDateTo: filterParams.startDateTo,
          updatedAtFrom: filterParams.updatedAtFrom,
          updatedAtTo: filterParams.updatedAtTo,
        },
        signal,
      ),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })
  const hasData = Boolean(data?.items && data.items.length > 0)

  const { mutate: toggleEventStatus, isPending: isToggling } = useMutation({
    mutationFn: (event: EventItem) => {
      const newStatus = event.status === 1 ? 0 : 1
      const payload: any = {
        name: event.name,
        eventType: event.eventType,
        mediaType: event.mediaType,
        description: event.description || '',
        status: newStatus,
        isChangeMediaContent: false,
      }

      if (event.startDate) payload.startDate = event.startDate
      if (event.endDate) payload.endDate = event.endDate

      return updateEvent(event.id, payload)
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật trạng thái thành công!'
      notificationApi.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      setTogglingEvent(null)
    },
    onError: () => {
      setTogglingEvent(null)
    },
  })

  const { mutate: removeEvent, isPending: isRemoving } = useMutation({
    mutationFn: (event: EventItem) => deleteEvent(event.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Xóa sự kiện thành công!'
      notificationApi.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
    onSettled: () => {
      setDeletingEvent(null)
    },
  })

  const handleDownload = async (url: string, fileName?: string) => {
    try {
      await downloadFileWithName({ url, fileName })
    } catch (error) {
      window.open(url, '_blank')
    }
  }

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        canView,
        canUpdate,
        onViewDetail: (event) => setViewingEvent(event),
        onUpdate: (event) => {
          setFormMode('update')
          setEditingEvent(event)
          setIsFormDrawerOpen(true)
        },
        onDelete: (event) => setDeletingEvent(event),
        onToggleStatus: (event) => setTogglingEvent(event),
      }),
    [pageIndex, pageSize, canView, canUpdate],
  )

  // Hook xuất file Excel chuẩn mẫu RobotsPage
  const { handleExport } = useTableExport<EventItem>(
    'Danh sách sự kiện',
    columns,
    async (payload) => {
          try {
            setIsExporting(true)
            await exportEvent(filterParams, payload, 'danh-sach-su kien.xlsx')
          } finally {
            setIsExporting(false)
          }
        },
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH SỰ KIỆN" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <>
      {contextHolder}
      <section className="space-y-4">
        <PageHeader
          title="DANH SÁCH SỰ KIỆN"
          onToggleFilter={() => setShowFilter((v) => !v)}
          onExport={canExport ? handleExport : undefined}
          disabled={!hasData}
          isExporting={isExporting}
        >
          {canCreate && (
            <Button
              variant="primary"
              size="md"
              className="flex-1 gap-2 h-10"
              onClick={() => {
                setFormMode('create')
                setEditingEvent(null)
                setIsFormDrawerOpen(true)
              }}
            >
              <Plus size={16} />
              Thêm mới
            </Button>
          )}
        </PageHeader>

        {showFilter && (
          <EventFilterPanel
            values={filter}
            onChange={setFilter}
            showUnitColumn={showUnitColumn}
            onSearch={() => {
              const trimmedFilter = {
                ...filter,
                keySearch: filter.keySearch.trim(),
              }
              setFilter(trimmedFilter)
              setActiveFilters(trimmedFilter)
              setPagination((s) => ({ ...s, pageIndex: 0 }))
              refetch()
            }}
          />
        )}

        <DataTable
          columns={columns}
          data={data?.items ?? []}
          tableOptions={{
            state: { pagination: { pageIndex, pageSize } },
            onPaginationChange: setPagination,
            getPaginationRowModel: getPaginationRowModel(),
            getCoreRowModel: getCoreRowModel(),
            manualPagination: true,
            pageCount: data?.totalPages,
          }}
          footer={
            <Pagination
              page={pageIndex + 1}
              pageCount={data?.totalPages ?? 1}
              pageSize={pageSize}
              totalCount={data?.total ?? 0}
              onPageChange={(p) =>
                setPagination((s) => ({ ...s, pageIndex: p - 1 }))
              }
              onPageSizeChange={(size) =>
                setPagination({ pageIndex: 0, pageSize: size })
              }
            />
          }
        />

        <EventDetailDrawer
          eventId={viewingEvent?.id || null}
          onClose={() => setViewingEvent(null)}
          onDownload={handleDownload}
          onUpdate={(event) => {
            setViewingEvent(null)
            setFormMode('update')
            setEditingEvent(event)
            setIsFormDrawerOpen(true)
          }}
        />

        {isFormDrawerOpen && (
          <EventFormDrawer
            isOpen={isFormDrawerOpen}
            mode={formMode}
            event={editingEvent}
            onClose={() => {
              setIsFormDrawerOpen(false)
              setEditingEvent(null)
            }}
          />
        )}

        <ConfirmModal
          isOpen={!!deletingEvent}
          title="Xóa sự kiện"
          description={<>Bạn chắc chắn muốn xóa sự kiện này?</>}
          variant="danger"
          isLoading={isRemoving}
          onClose={() => setDeletingEvent(null)}
          onConfirm={() => deletingEvent && removeEvent(deletingEvent)}
        />

        <ConfirmModal
          isOpen={!!togglingEvent}
          title="Xác nhận"
          description={
            <>
              Bạn chắc chắn muốn{' '}
              {togglingEvent?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} sự
              kiện <strong>{togglingEvent?.name}</strong> này?
            </>
          }
          variant="info"
          isLoading={isToggling}
          onClose={() => setTogglingEvent(null)}
          onConfirm={() => togglingEvent && toggleEventStatus(togglingEvent)}
        />
      </section>
    </>
  )
}
